import React, { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Shield, Download, Printer, ArrowLeft, Sparkles } from "lucide-react";
import AnalysisExtras from "../components/AnalysisExtras";

/**
 * Full printable credit consultation report.
 * URL: /report/:documentId  (optional ?print=1 to auto-trigger print dialog on load)
 *
 * Print CSS hides the site navbar/footer/modals so the PDF is clean.
 * Both client (owner) and admin can view any report.
 */
const PRIORITY_STYLES = {
  high: "bg-red-100 text-red-700 border-red-300",
  medium: "bg-amber-100 text-amber-700 border-amber-300",
  low: "bg-gray-100 text-gray-700 border-gray-300",
};

export default function CreditReport() {
  const { documentId } = useParams();
  const [params] = useSearchParams();
  const autoPrint = params.get("print") === "1";
  const { user } = useAuth();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const printedRef = useRef(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data: a } = await api.get(`/documents/${documentId}/analysis`);
        if (!mounted) return;
        setAnalysis(a?.analysis?.result || null);
      } catch (e) {
        if (mounted) setError(e?.response?.data?.detail || "Couldn't load the report.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [documentId]);

  // Auto-print once analysis is loaded (when ?print=1)
  useEffect(() => {
    if (autoPrint && analysis && !printedRef.current) {
      printedRef.current = true;
      // brief delay so fonts/images settle
      const t = setTimeout(() => window.print(), 700);
      return () => clearTimeout(t);
    }
  }, [autoPrint, analysis]);

  const onPrint = () => window.print();

  if (loading) {
    return <div className="section dark-zone text-center text-sm text-white/50 py-20">Loading report…</div>;
  }

  if (error || !analysis) {
    return (
      <div className="section dark-zone text-center py-20">
        <p className="text-white/70 mb-3">{error || "No analysis yet for this document."}</p>
        <Link to="/dashboard" className="btn-outline !py-2 !px-4 !text-[12px]"><ArrowLeft className="w-3.5 h-3.5" /> Back to dashboard</Link>
      </div>
    );
  }

  const meta = analysis.report_meta || {};
  const summary = analysis.summary || {};
  const items = analysis.negative_items || [];
  const today = new Date().toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });

  return (
    <>
      {/* print-only styles — keep the report clean when saving to PDF */}
      <style>{`
        @media print {
          body { background: #fff !important; }
          nav, footer, [data-testid="lead-capture-modal"], [data-testid="public-chat-widget"], [data-no-print] { display: none !important; }
          .credit-report-print { background: #fff !important; color: #111 !important; padding: 24px !important; }
          .credit-report-print h1, .credit-report-print h2, .credit-report-print h3 { color: #0f1d3a !important; }
          .credit-report-print .print-card { background: #fff !important; border: 1px solid #cfd6e2 !important; color: #111 !important; box-shadow: none !important; }
          .credit-report-print .print-meta { color: #555 !important; }
          .credit-report-print .print-gold { color: #b48a3e !important; }
          .credit-report-print .print-section-title { color: #b48a3e !important; }
          .credit-report-print .print-text { color: #1f1f1f !important; }
          .credit-report-print .print-muted { color: #6b6b6b !important; }
          .credit-report-print { font-family: 'Inter', Georgia, serif !important; }
          /* unwind dark-theme utilities by mapping their CSS vars to print-safe values */
          .credit-report-print { --if-gold: #b48a3e; }
          @page { margin: 0.6in; }
        }
      `}</style>

      <div className="section dark-zone py-8 credit-report-print" data-testid="full-report">
        {/* Toolbar (hidden on print) */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-2" data-no-print>
          <Link to="/dashboard" className="btn-outline !py-2 !px-3 !text-[11px]"><ArrowLeft className="w-3.5 h-3.5" /> Back to dashboard</Link>
          <div className="flex gap-2">
            <button onClick={onPrint} className="btn-outline !py-2 !px-3 !text-[11px]" data-testid="print-report"><Printer className="w-3.5 h-3.5" /> Print</button>
            <button onClick={onPrint} className="btn-gold !py-2 !px-3 !text-[11px]" data-testid="download-report-pdf"><Download className="w-3.5 h-3.5" /> Save as PDF</button>
          </div>
        </div>

        {/* Letterhead */}
        <div className="flex items-start justify-between gap-4 pb-5 border-b border-white/15 mb-6 print-card">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-5 h-5 text-[var(--if-gold)] print-gold" />
              <span className="font-display text-xl text-[var(--if-gold)] print-gold">Infantry Financial</span>
            </div>
            <h1 className="font-display text-3xl text-white print-text" data-testid="report-title">Credit Consultation Report</h1>
            <p className="text-sm text-white/55 print-meta mt-1" style={{ fontFamily: "Inter, sans-serif" }}>
              Prepared for {meta.consumer_name || user?.name || user?.email || "Client"} · {today}
            </p>
          </div>
          <div className="text-right print-meta">
            <div className="text-[10px] uppercase tracking-wider text-white/45 mb-1" style={{ fontFamily: "Inter, sans-serif" }}>Analyst</div>
            <div className="text-sm text-white print-text" style={{ fontFamily: "Inter, sans-serif" }}>Sergeant Credit AI</div>
            <div className="text-[10px] text-white/45 print-meta mt-2">Powered by GPT-5.2 · FCRA-aligned</div>
          </div>
        </div>

        {/* Executive summary */}
        <div className="mb-6 p-4 rounded-lg border border-[var(--if-gold)]/30 bg-[var(--if-gold)]/[0.04] print-card">
          <div className="font-mono-label text-[var(--if-gold)] print-section-title mb-2">EXECUTIVE SUMMARY</div>
          {summary.headline && (
            <p className="text-base text-white print-text italic mb-2" style={{ fontFamily: "Inter, sans-serif" }}>&ldquo;{summary.headline}&rdquo;</p>
          )}
          {summary.narrative && (
            <p className="text-sm text-white/80 print-text leading-relaxed" style={{ fontFamily: "Inter, sans-serif" }}>{summary.narrative}</p>
          )}
          <div className="grid grid-cols-3 gap-3 mt-4">
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-wider text-white/45 print-meta">Negative items</div>
              <div className="font-display text-3xl text-white print-text">{summary.total_negative_items ?? items.length}</div>
            </div>
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-wider text-white/45 print-meta">Score impact</div>
              <div className="font-display text-xl text-[var(--if-gold)] print-gold uppercase">{summary.estimated_score_impact || "—"}</div>
            </div>
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-wider text-white/45 print-meta">Bureaus</div>
              <div className="font-display text-sm text-white print-text">{(meta.bureaus_present || []).join(" · ") || "—"}</div>
            </div>
          </div>
          {(meta.credit_scores || []).length > 0 && (
            <div className="flex flex-wrap justify-center gap-2 mt-4">
              {meta.credit_scores.map((s) => (
                <div key={`${s.bureau}-${s.score}`} className="px-3 py-1.5 rounded-full bg-black/30 border border-white/10 text-xs print-card" style={{ fontFamily: "Inter, sans-serif" }}>
                  <span className="text-white/55 print-muted">{s.bureau}:</span>{" "}
                  <span className="text-[var(--if-gold)] print-gold font-semibold">{s.score}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* All the new analytical sections (score factors, util, history, plan, payoff, funding) */}
        <AnalysisExtras r={analysis} />

        {/* Detailed dispute list */}
        {items.length > 0 && (
          <div className="mt-6 p-4 rounded-lg bg-black/20 border border-white/5 print-card">
            <div className="font-mono-label text-[var(--if-gold)] print-section-title mb-3 flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> Dispute opportunities
            </div>
            <div className="space-y-2">
              {items.map((it) => (
                <div key={it.id} className="p-3 rounded border border-white/10 bg-black/10 print-card" data-testid={`report-neg-${it.id}`}>
                  <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
                    <div className="font-display text-base text-white print-text">{it.creditor}{it.account_last4 ? ` ····${it.account_last4}` : ""}</div>
                    <div className="flex gap-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${PRIORITY_STYLES[it.priority] || PRIORITY_STYLES.low}`}>
                        {(it.priority || "low").toUpperCase()}
                      </span>
                      <span className="text-[10px] text-white/55 print-muted uppercase tracking-wider">{it.bureau}</span>
                    </div>
                  </div>
                  <div className="text-xs text-white/65 print-muted mb-1" style={{ fontFamily: "Inter, sans-serif" }}>
                    {it.status}{it.amount_usd ? ` · $${it.amount_usd.toLocaleString()}` : ""}{it.date_reported ? ` · reported ${it.date_reported}` : ""}
                  </div>
                  <p className="text-sm text-white/85 print-text mt-1" style={{ fontFamily: "Inter, sans-serif" }}>{it.why_negative}</p>
                  {it.dispute_angle && (
                    <p className="text-xs text-[var(--if-gold)] print-gold mt-1" style={{ fontFamily: "Inter, sans-serif" }}>
                      Strategy: {it.dispute_angle}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Disclaimer */}
        {analysis.disclaimer && (
          <div className="mt-8 pt-4 border-t border-white/10 text-[10px] text-white/45 print-muted leading-relaxed" style={{ fontFamily: "Inter, sans-serif" }}>
            <p><strong>Disclaimer.</strong> {analysis.disclaimer} Infantry Financial does not guarantee score increases or specific outcomes. This document is for informational purposes only and does not constitute legal, tax, or financial advice. CROA — 15 U.S.C. § 1679; FCRA — 15 U.S.C. § 1681.</p>
          </div>
        )}

        <div className="mt-6 text-center text-[10px] text-white/40 print-muted" style={{ fontFamily: "Inter, sans-serif" }}>
          🎖 Infantry Financial · https://infantry-financial.com · This report was generated on {today}
        </div>
      </div>
    </>
  );
}
